import React, { useRef, useState, useEffect } from "react";
import { ButtonContained, ButtonOutlined, ButtonText, } from "../../../components";
import S from "../styles/StylesWebcam";

import { hooks } from "../../../utils";
import { StorageContext } from "../../../contexts/StorageContext";
import { api } from "../../../services";

import { CameraAlt, Face } from "@mui/icons-material";
import AnteriorTab from "./AnteriorTab";
import CameraTab from "./CameraTab";

function WebcamModal({
  onSubmit,
  data,
  userId = "",
  onBack,
  docType,
  docValue,
  guestImage,
}: {
  onSubmit: any;
  data?: any;
  userId?: String | null;
  onBack: () => void;
  docType?: number;
  docValue?: string;
  guestImage?: string;
}) {
  const { loading, call } = hooks.useRequest();
  const storage = React.useContext<any>(StorageContext);
  const facialFormat = data?.find(
    item => item.type === "FACIAL_FORMAT"
  );
  const hasUploadImage = data?.find(
    item => item.type === "FACIAL_MANUAL_UPLOAD_ENABLED"
  )?.value === 'true';

  const [imgSrc, setImgSrc] = useState<{ src: string; name: string } | null>(
    guestImage ? { src: guestImage, name: "saved-photo" } : null
  );
  const [activeTab, setActiveTab] = useState<"camera" | "anterior">("camera");
  const [anteriorOptions, setAnteriorOptions] = useState<{ imageId: string; name: string }[]>([]);
  const [selectedAnteriorId, setSelectedAnteriorId] = useState<string>("");
  const [anteriorImageSrc, setAnteriorImageSrc] = useState<string | null>(null);

  const [cameraRequested, setCameraRequested] = useState(false);
  const [errorImageValidation, setErrorImageValidation] = useState("");
  const [loadingValidation, setLoadingValidation] = useState<boolean>(false);
  const latestImageIdRef = useRef<string>("");

  function removerBase64EAntes(texto) {
    if (!texto) return null;
    const indiceBase64 = texto.indexOf("base64,");
    if (indiceBase64 !== -1) return texto.substring(indiceBase64 + 7);
    return texto;
  }

  const sendData = async () => {
    setLoadingValidation(true);
    const response = await api.validateImage({
      id: userId,
      image: removerBase64EAntes(imgSrc?.src),
    });
    const imageisValid = response.status !== 200 ? true : response.data.isValid;
    if (!imageisValid) {
      setErrorImageValidation(response.data.error);
      return;
    }
    setLoadingValidation(false);
    setErrorImageValidation("");
    onSubmit(imgSrc);
  };

  const downloadAnteriorImage = (imageId: string) => {
    latestImageIdRef.current = imageId;
    const cacheKey = `anterior-image-${imageId}`;
    const cached = storage.getData(cacheKey);
    if (cached?.data) {
      setAnteriorImageSrc(cached.data);
      return;
    }

    call(cacheKey, api.getImage(imageId), (res: any) => {
      if (latestImageIdRef.current === imageId) {
        if (res?.data) setAnteriorImageSrc(res.data);
      }
    });
  };

  useEffect(() => {
    if (!docValue) return;
    const fetchAnteriorOptions = async () => {
      const cleanCpf = docValue.replace(/\D/g, "");
      call(null, api.findByCpf(cleanCpf), (response) => {
        if (response.ok && Array.isArray(response.data)) {
          const options = response.data
            .filter((g: any) => g.imageId)
            .map((g: any) => ({ imageId: g.imageId, name: g.name ?? "" }));
          setAnteriorOptions(options);
          if (options.length > 0) {
            setSelectedAnteriorId(options[0].imageId);
            downloadAnteriorImage(options[0].imageId);
          }
        }
      });
    };
    const fetchPassportAnteriorOptions = async () => {
      const cleanCpf = docValue.replace(/\D/g, "");
      call(null, api.findByPassport(cleanCpf), (response) => {
        if (response.ok && Array.isArray(response.data)) {
          const options = response.data
            .filter((g: any) => g.imageId)
            .map((g: any) => ({ imageId: g.imageId, name: g.name ?? "" }));
          setAnteriorOptions(options);
          console.log(options)
          if (options.length > 0) {
            setSelectedAnteriorId(options[0].imageId);
            downloadAnteriorImage(options[0].imageId);
          }
        }
      });
    };
    if (docType === 1) {
      fetchAnteriorOptions();
    } else {
      fetchPassportAnteriorOptions()
    }

  }, []);

  const handleAnteriorSelect = (imageId: string) => {
    setSelectedAnteriorId(imageId);
    setAnteriorImageSrc(null);
    if (!imageId) return;
    downloadAnteriorImage(imageId);
  };

  const handleTabChange = (tab: "camera" | "anterior") => {
    setActiveTab(tab);
    setErrorImageValidation("");
    setLoadingValidation(false);
  };

  const renderCapturedImage = () => (
    <S.Container>
      <S.CapturedPhoto $src={imgSrc?.src} />

      <S.Actions>
        <ButtonContained
          disabled={errorImageValidation !== "" || loadingValidation}
          onClick={sendData}
        >
          <span >{errorImageValidation !== "" ? "Erro na validação" : loadingValidation ? "Validando..." : "Continuar"}</span>
        </ButtonContained>

        <ButtonOutlined
          onClick={() => {
            setImgSrc(null);
            setErrorImageValidation("");
            setLoadingValidation(false);
          }}
        >
          Tirar nova foto
        </ButtonOutlined>

        <ButtonText onClick={onBack}>
          Voltar
        </ButtonText>
      </S.Actions>
    </S.Container>
  );

  return (
    <S.OuterWrapper >
      <S.TabRow>
        <S.TabButton
          $active={activeTab === "camera"}
          $position="left"
          onClick={() => handleTabChange("camera")}
        >
          <CameraAlt />
          Câmera do Dispositivo
        </S.TabButton>
        <S.TabButton
          $active={activeTab === "anterior"}
          $position="right"
          onClick={() => handleTabChange("anterior")}
        >
          <Face />
          Facial Anterior
        </S.TabButton>
      </S.TabRow>

      <S.ContentCard>
        {activeTab === "anterior" ? (
          <AnteriorTab
            facialFormat={facialFormat?.value}
            loading={loading}
            anteriorOptions={anteriorOptions}
            selectedAnteriorId={selectedAnteriorId}
            anteriorImageSrc={anteriorImageSrc}
            onSelectAnterior={handleAnteriorSelect}
            onSubmit={onSubmit}
            onBack={onBack}
            showUploadImage={hasUploadImage}
          />
        ) : imgSrc?.src ? (
          renderCapturedImage()
        ) : (
          <CameraTab
            facialFormat={facialFormat?.value}
            cameraRequested={cameraRequested}
            onRequestCamera={() => setCameraRequested(true)}
            onCapture={setImgSrc}
            onBack={onBack}
          />
        )}
      </S.ContentCard>
    </S.OuterWrapper>
  );
}

export default WebcamModal;
